import {Component, Inject, OnDestroy} from '@angular/core';
import {MAT_DIALOG_DATA, MatDialog, MatDialogRef} from "@angular/material/dialog";
import {EnvironmentElement} from "../../../ParseClass/environment-element";
import {
    aggregateHardawareDetailDevice,
    arrayIsSet, ElementTreeType,
    objectIsSet, peiodicDataElementType, peiodicDataElementTypeValue, stringIsSet
} from "../../../providers/service/global-variables.service";
import {ElementTypeParse} from "../../../ParseClass/ElementType.Parse";
import {Observable, Subscription} from "rxjs";
import {ChartDataTypesComponent} from "../chart-data-types/chart-data-types.component";
import {AllDevicesService} from "../../../providers/service/all-devices.service";
import {SensorElement} from "../../../ParseClass/sensor-element";
import {ShowRawDetailComponent} from "../show-raw-detail/show-raw-detail.component";
import {TranslateService} from "@ngx-translate/core";
import {formatDate} from "@angular/common";
import {FormBuilder, FormGroup, Validators} from "@angular/forms";
import {map, startWith} from "rxjs/operators";

export type ResponseSensorList = {
    setPoint: number,
    selectedSensors: SensorElement[]
}

@Component({
    selector: 'app-sensors-list',
    templateUrl: './sensors-list.component.html',
    styleUrls: ['./sensors-list.component.scss'],
    standalone: false
})
export class SensorsListComponent implements OnDestroy {
    public formGroup: FormGroup<any>
    editEnabled$: Observable<boolean>
    private subscriptions = new Subscription()
    isEditable

    constructor(
        private dialog: MatDialog,
        public dialogRef: MatDialogRef<SensorsListComponent>,
        @Inject(MAT_DIALOG_DATA) public data: {
            energyElement: EnvironmentElement,
            elements: ElementTreeType[] | undefined,
            isEditable: boolean | undefined
        },
        private allDevicesService: AllDevicesService,
        private translateService: TranslateService,
        private fb: FormBuilder) {
        this.formGroup = this.fb.group({
            editEnabled: false,
            selectedSensors: [undefined, [Validators.required]],
            setPoint: [undefined, [Validators.required]],
        });
        this.isEditable = this.data.isEditable == true
        this.editEnabled$ = this.formGroup.get('editEnabled')!.valueChanges.pipe(
            startWith(undefined),
            map((value) => value == true)
        )

        this.subscriptions.add(
            this.editEnabled$.subscribe(enable => {
                const keysOnOff = ['selectedSensors', 'setPoint']
                if (enable) {
                    keysOnOff.forEach(key => {
                        this.formGroup.get(key)?.enable();
                    })
                } else {
                    keysOnOff.forEach(key => {
                        this.formGroup.get(key)?.disable();
                    })
                }
            })
        )
    }

    ngOnDestroy() {
        this.subscriptions.unsubscribe();
    }

    get editEnabled() {
        return this.formGroup.get('editEnabled')!.value
    }

    set editEnabled(value) {
        this.formGroup.get('editEnabled')!.setValue(value)
    }

    getInfoValuesPredicate(child: EnvironmentElement): SensorElement[] | undefined {
        if (child && arrayIsSet(child.sensors)) {
            return child.sensors;
        } else {
            return undefined;
        }
    }

    public openDialogChartSensorDataTypes(sensoElement: SensorElement,): Observable<any> {

        const selectElement = new ElementTreeType(sensoElement.objectId, sensoElement.name, sensoElement.info, undefined, sensoElement.recentSensorData)
        const dialogRef = this.dialog.open(ChartDataTypesComponent, {
            minWidth: '100vw',
            height: '100vh',
            data: {selectElement, elements: this.data.elements},
            autoFocus: false,
            exitAnimationDuration: 0.1
        });
        return dialogRef.afterClosed();
    }

    openShowRawDetail(values: {
        title: { traduction?: string, value?: any },
        value: { traduction?: string, value?: any }
    }[], generalTitle: string | undefined = undefined, removeValues = false): Observable<{
        title: { traduction?: string, value?: any },
        value: { traduction?: string, value?: any }
    }[] | undefined> {
        let resizeClass;
        let minWidth = '100vw';
        if (window.innerWidth < 500) {
            minWidth = '100%';
            resizeClass = '';
        } else {
            minWidth = '300px';
            resizeClass = '';
        }
        const dialogRef = this.dialog.open(ShowRawDetailComponent, {
            maxHeight: '100vh',
            minWidth,
            panelClass: 'huna-height-panel',
            data: {values, resizeClass, title: generalTitle, removeValues},
            autoFocus: false,
            exitAnimationDuration: 0.1
        });
        return dialogRef.afterClosed();
    }

    getValueForTraduction(device: aggregateHardawareDetailDevice, key: string): string {
        const getValue = (device: aggregateHardawareDetailDevice, key: string) => {
            const index = device.sensorDataTypes.findIndex(type => type === key);
            if (index >= 0) {
                const periodicElementType = (peiodicDataElementType as any)[key];
                if (periodicElementType != null) {
                    const unitTraductionKey = stringIsSet(periodicElementType.unitOfMeasurementKeyInTraduction) ? periodicElementType.unitOfMeasurementKeyInTraduction : key;
                    let unit;
                    if (stringIsSet(unitTraductionKey)) {
                        unit = this.translateService.instant('unitOfMeasurement.' + unitTraductionKey + '.unitMeasure')
                        unit = unit.includes('unitOfMeasurement') ? '' : unit;
                    }
                    if (periodicElementType.type === 'bool') {
                        return device.sensorDataValues[index] === 1 ? this.translateService.instant('yes') : this.translateService.instant('no');
                    } else if (periodicElementType.type === 'number') {
                        return Math.round(device.sensorDataValues[index] * 10) / 10 + unit;
                    }
                    return device.sensorDataValues[index] + unit;
                } else {
                    return undefined;
                }

            } else {
                return undefined;
            }
        };

        if (device) {
            return getValue(device, key);
        } else {
            return '';
        }
    }

    openInfoDevice(sensorEvent: SensorElement): void {
        const device = this.allDevicesService.convertSensorElementInAggregateData(sensorEvent);
        const values: {
            title: { traduction?: string, sensorTraduction?: string, value?: any },
            value: { traduction?: string, sensorTraduction?: string, value?: any }
        }[] = [];
        if (device) {
            device.sensorDataTypes.forEach(type => {
                const title = {sensorTraduction: type};
                const value = {value: this.getValueForTraduction(device, type)};
                values.push({title, value});
            });

            let lastDate: Date | undefined;
            if (arrayIsSet(device.sensorDataDates)) {
                lastDate = device.sensorDataDates.reduce((prev, date) => {
                    if (prev == null) {
                        prev = date;
                    } else if (prev.getTime() < date.getTime()) {
                        prev = date;
                    }
                    return prev;
                }, undefined as Date | undefined)
                const title = {traduction: 'IotDevices.lastComm'}
                if (lastDate != null) {
                    const mediumDate = formatDate(lastDate, 'mediumDate', this.translateService.currentLang);
                    const shortTime = formatDate(lastDate, 'shortTime', this.translateService.currentLang);
                    values.unshift({
                        title,
                        value: {value: mediumDate + ' ' + shortTime}
                    })
                }
            }
            this.openShowRawDetail(values, undefined, undefined).subscribe();
        }
    }

    clickEdit() {
        this.editEnabled = !this.editEnabled;
    }

    saveValue() {
        if (this.formGroup.valid) {
            const setPoint = this.formGroup.get('setPoint')?.value;
            let selectedSensors;
            if (arrayIsSet(this.formGroup.get('selectedSensors')!.value)) {
                selectedSensors = this.formGroup.get('selectedSensors')!.value.map((id: string) => {
                    return this.data.energyElement.sensors.find(sensor => sensor.objectId == id)
                })
            }
            this.dialogRef.close({selectedSensors, setPoint})
        }

    }
}
